import type { FetchResponse, Interceptor, RequestParams } from '../rest-service';
import { mapToFetchResponse } from '../utils/map-to-fetch-response';

const HTTP_INTERNAL_SERVER_ERROR = 500;
const HTTP_BAD_GATEWAY = 502;
const HTTP_SERVICE_UNAVAILABLE = 503;

const MAX_RETRY_COUNT = 3;

export const retryInterceptor: Interceptor = {
  async interceptorRequest(params) {
    return params;
  },

  async interceptorResponse(response, requestParams) {
    const statusToRetry = [HTTP_INTERNAL_SERVER_ERROR, HTTP_BAD_GATEWAY, HTTP_SERVICE_UNAVAILABLE];
    const request = response.meta?.request;

    let newParsedResponse: FetchResponse | undefined;
    let status = response.meta?.response?.status;
    let i = 0;
    while (statusToRetry.includes(status) && i < MAX_RETRY_COUNT) {
      newParsedResponse = await schedulesAttemptRequest(request, requestParams, getDelayTime(i));
      status = newParsedResponse?.meta?.response?.status;
      i++;
    }

    return newParsedResponse ?? response;
  },
};

/**
 * Initiates the timed promise to attempt the fetch
 * @param request request data
 * @param requestParams request params
 * @param delay intended delay for each retry (exponentially increased)
 * @returns the resolved response promise
 */
async function schedulesAttemptRequest(request: Request, requestParams: RequestParams, delay: number) {
  return await new Promise<FetchResponse>((resolve) => {
    setTimeout(async () => {
      const response = await attemptRequest(request, requestParams);
      resolve(response);
    }, delay);
  });
}

/**
 * Attempts to fetch the originally intended response
 * @param request request data
 * @param requestParams request params
 * @returns the newly fetched response, parsed to internal fetch format
 */
async function attemptRequest(request: Request, requestParams: RequestParams) {
  const response = await fetch(request.clone());
  return await mapToFetchResponse(request, requestParams, response);
}

/**
 * Determines the timeout delay for each retry. Math.pow's return must be in seconds.
 * @param base power operation's base
 * @param exponent power operation's exponent
 * @returns the delay in milliseconds
 */
function getDelayTime(base: number, exponent = 2) {
  return Math.pow(base, exponent) * 1000;
}
